/**
 * Offline cache coherence for multi-instance Cooling System (#996 Phase 2).
 * Keeps request.coolingSystemConfigurations aligned with the cooling system API / React Query.
 * Mirrors smallUpsOfflineSync.
 */

import {
  offlineCache,
  type OfflineCoolingSystemOperation,
} from './offlineCache';
import { applyPendingRepeatableOperations } from './repeatableSectionOfflineSync';
import { groupCoolingSystemsByStatus } from '../components/coolingsystems/coolingSystemListUtils';

import type {
  GroupedCoolingSystemConfigurations,
  CoolingSystemConfiguration,
} from '../types/coolingSystem';

/** Cooling system list from cached request detail (offline reads). */
export async function getCachedCoolingSystemList(
  requestId: string,
): Promise<CoolingSystemConfiguration[]> {
  const cachedRequest = await offlineCache.getCachedRequestById(requestId);
  return cachedRequest?.coolingSystemConfigurations ?? [];
}

/** Grouped units derived from cached list (same shape as GET …/cooling-systems/grouped). */
export async function getCachedCoolingSystemGrouped(
  requestId: string,
): Promise<GroupedCoolingSystemConfigurations> {
  return groupCoolingSystemsByStatus(
    await getCachedCoolingSystemList(requestId),
  );
}

/** Single unit from cached list when offline. */
export async function getCachedCoolingSystemById(
  requestId: string,
  coolingSystemId: number,
): Promise<CoolingSystemConfiguration | undefined> {
  return (await getCachedCoolingSystemList(requestId)).find(
    unit => unit.id === coolingSystemId,
  );
}

/**
 * After a successful online cooling system fetch or mutation refetch, mirror units onto
 * the cached request so offline reads stay consistent.
 */
export async function mirrorCoolingSystemToOfflineRequestCache(
  requestId: string,
  coolingSystems: CoolingSystemConfiguration[],
): Promise<void> {
  const cachedRequest = await offlineCache.getCachedRequestById(requestId);
  if (!cachedRequest) {
    return;
  }
  await offlineCache.updateCachedRequestCoolingSystems(
    requestId,
    coolingSystems,
  );
}

/** Pending offline cooling system queue entries for one request (create/update/delete/reorder). */
export async function getPendingCoolingSystemOpsForRequest(
  requestId: string,
): Promise<
  Awaited<ReturnType<typeof offlineCache.getPendingCoolingSystemOperations>>
> {
  const pending = await offlineCache.getPendingCoolingSystemOperations();
  return pending.filter(op => String(op.requestId) === String(requestId));
}

/**
 * In-flight offline cooling system ops (status `pending` OR `syncing`) for one request.
 * Used by the ONLINE overlay: the sync flips an op to `syncing` BEFORE its POST,
 * so a `pending`-only view drops an op mid-sync and the item vanishes during the
 * reconnect race. Including `syncing` keeps it visible until the op is dequeued.
 */
export async function getInFlightCoolingSystemOpsForRequest(
  requestId: string,
): Promise<OfflineCoolingSystemOperation[]> {
  const queue = await offlineCache.getOfflineCoolingSystemQueue();
  return queue.filter(
    op =>
      (op.status === 'pending' || op.status === 'syncing') &&
      String(op.requestId) === String(requestId),
  );
}

/**
 * Overlay pending offline cooling system operations onto a base list so offline reads
 * reflect not-yet-synced changes. Pure and deterministic (ops applied in
 * timestamp order). De-dupes creates by id so an optimistic unit already written
 * into the base list is not duplicated.
 */
export function applyPendingCoolingSystemOperations(
  requestId: string,
  base: CoolingSystemConfiguration[],
  ops: OfflineCoolingSystemOperation[],
): CoolingSystemConfiguration[] {
  return applyPendingRepeatableOperations(requestId, base, ops, {
    instanceId: op => op.coolingSystemId,
    reorderIds: op => op.coolingSystemIds,
  });
}

/** Cached cooling system list with pending offline operations overlaid (offline reads). */
export async function getOfflineCoolingSystemList(
  requestId: string,
): Promise<CoolingSystemConfiguration[]> {
  const [base, ops] = await Promise.all([
    getCachedCoolingSystemList(requestId),
    getPendingCoolingSystemOpsForRequest(requestId),
  ]);
  return applyPendingCoolingSystemOperations(requestId, base, ops);
}

/** Grouped units (existing/new) from the overlaid offline list. */
export async function getOfflineCoolingSystemGrouped(
  requestId: string,
): Promise<GroupedCoolingSystemConfigurations> {
  return groupCoolingSystemsByStatus(
    await getOfflineCoolingSystemList(requestId),
  );
}

/** Single unit from the overlaid offline list (includes pending creates). */
export async function getOfflineCoolingSystemById(
  requestId: string,
  coolingSystemId: number,
): Promise<CoolingSystemConfiguration | undefined> {
  return (await getOfflineCoolingSystemList(requestId)).find(
    unit => unit.id === coolingSystemId,
  );
}
